/**
 * useKpiSnapshot — una `get` del documento snapshot del ruolo corrente.
 *
 * Non è state app-wide: vive sulla Home e basta. useState per-istanza
 * è il pattern giusto (vedi useDashboardData). Nessun fallback live:
 * assente, stantio o non disponibile restano tali.
 */

import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import {
  KPI_SNAPSHOT_STALE_AFTER_MS,
  type KpiSnapshotDoc,
} from '@shared/dashboard/kpi-snapshot';
import {
  fetchKpiSnapshot,
  kpiSnapshotScopeId,
} from '../../../services/kpi-snapshot';

export type KpiSnapshotState =
  | { kind: 'loading' }
  | { kind: 'ready'; doc: KpiSnapshotDoc; stale: boolean }
  | { kind: 'absent' }
  | { kind: 'unavailable' };

export function isSnapshotStale(
  generatedAtMillis: number,
  nowMs: number,
): boolean {
  return nowMs - generatedAtMillis > KPI_SNAPSHOT_STALE_AFTER_MS;
}

/**
 * Orario dello snapshot in italiano, fuso Europe/Rome.
 * Esempio: `{ time: '04:30', date: '19/08/2026' }`.
 */
export function formatKpiSnapshotOrario(iso: string): {
  time: string;
  date: string;
} {
  const d = new Date(iso);
  const time = new Intl.DateTimeFormat('it-IT', {
    timeZone: 'Europe/Rome',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
  const date = new Intl.DateTimeFormat('it-IT', {
    timeZone: 'Europe/Rome',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d);
  return { time, date };
}

export function useKpiSnapshot(): KpiSnapshotState {
  const { role, dietologaScopeUid } = useAuth();
  // Lo snapshot della dietologa e' indicizzato per uid: un account di
  // backoffice con delega deve leggere quello del delegante, altrimenti
  // chiederebbe un documento che non esiste.
  const scopeId = kpiSnapshotScopeId(role, dietologaScopeUid);
  const [fetched, setFetched] = useState<KpiSnapshotState>({ kind: 'loading' });
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!scopeId) return;

    const id = scopeId;
    let cancelled = false;

    async function load() {
      setFetched({ kind: 'loading' });
      try {
        const snapshot = await fetchKpiSnapshot(id);
        if (cancelled || !mountedRef.current) return;
        if (!snapshot) {
          setFetched({ kind: 'absent' });
          return;
        }
        setFetched({
          kind: 'ready',
          doc: snapshot,
          stale: isSnapshotStale(snapshot.generatedAtMillis, Date.now()),
        });
      } catch {
        if (cancelled || !mountedRef.current) return;
        setFetched({ kind: 'unavailable' });
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [scopeId]);

  if (!scopeId) return { kind: 'unavailable' };
  return fetched;
}
